<template>
  <v-app>
    <!-- Left Nav -->
    <v-navigation-drawer v-model="navigationDrawer" app temporary>
      <v-list-item>
        <v-list-item-content>
          <v-list-item-title class="d-flex align-center">
            <router-link :to="{ name: 'home' }">
              <v-avatar size="40" tile>
                <v-img :src="appLogoUrl" :alt="appName" />
              </v-avatar>
            </router-link>
            <span class="ml-2">{{ appName }}</span>
          </v-list-item-title>
        </v-list-item-content>
      </v-list-item>

      <v-list-item>
        <v-list-item-content>
          <v-list-item-title class="title">
            <v-chip
              color="success"
              v-if="
                user &&
                  (is_user_admin ||
                    is_user_seller ||
                    is_user_supervisor ||
                    is_user_manager ||
                    is_user_customer_service ||
                    is_user_financial_supervisor ||
                    is_user_financial_assistant)
              "
            >
              {{ user_role_text }}
            </v-chip>
            <span v-else>{{ $t("Navigation") }}</span>
          </v-list-item-title>
        </v-list-item-content>
      </v-list-item>
      <v-divider />

      <!-- Estos menu links son para los Admin, Sellers, Supervisores, Managers y Servicio al cliente     -->
      <template
        v-if="
          user &&
            (is_user_admin ||
              is_user_seller ||
              is_user_supervisor ||
              is_user_manager ||
              is_user_customer_service ||
              is_user_financial_supervisor ||
              is_user_financial_assistant)
        "
      >
        <v-list dense>
          <menu-item
            v-for="navItem in navAdminItems"
            :navItem="navItem"
            :key="navItem.title"
          />
        </v-list>
        <v-divider />
      </template>

      <v-list dense v-if="!is_user_tracking">
        <menu-item
          v-for="navUserItem in navUserItems"
          :navItem="navUserItem"
          :key="navUserItem.title"
        />
        <template v-if="authenticated">
          <menu-item
            v-for="(navAuthUserItem, key) in navAuthUserItems"
            :navItem="navAuthUserItem"
            :key="navAuthUserItem.title"
          />
          <v-list-group
            prepend-icon="mdi-cards-playing-outline"
            no-action
            v-if="config('app.enable_casino_games')"
          >
            <template v-slot:activator>
              <v-list-item-title>{{ $t("Games") }}</v-list-item-title>
            </template>
            <template
              v-for="(game, id) in games"
              v-if="
                ![
                  'lottery',
                  'e-game-american-roulette-3d',
                  'sports-bet'
                ].includes(id)
              "
            >
              <v-list-item
                v-if="!config(id + '.variations')"
                :key="id"
                :to="{ name: 'game', params: { game: id } }"
                link
                exact
              >
                <v-list-item-content>
                  <v-list-item-title>{{ game.name }}</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
              <template v-else>
                <template v-for="variation in config(id + '.variations')">
                  <v-list-item
                    :key="variation.slug"
                    :to="{
                      name: 'game',
                      params: { game: id, slug: variation.slug }
                    }"
                    link
                    exact
                  >
                    <v-list-item-content>
                      <v-list-item-title>{{
                        variation.title
                      }}</v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                </template>
              </template>
            </template>

            <!-- External Games -->

            <v-list-item
              v-for="externalGame in externalGames"
              :key="externalGame.id"
              :to="externalGame.to"
              link
              exact
            >
              <v-list-item-content>
                <v-list-item-title>{{ externalGame.title }}</v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </v-list-group>
        </template>
      </v-list>
    </v-navigation-drawer>

    <!-- Chat -->
    <chat
      v-if="authenticated && chatEnabled && chatClicked"
      v-model="chatDrawer"
      @message="setUnreadChatMessagesCount"
    />

    <!-- Banner -->
    <v-system-bar window fixed app height="auto" class="py-3" v-if="showBanner">
      <v-spacer />
      <b>{{ bannerMessage }}</b>
      <v-spacer />
      <v-icon @click="closeBanner">mdi-close</v-icon>
    </v-system-bar>

    <!-- Floating button with contact info -->
    <floating-button />

    <!-- App Bar -->
    <v-app-bar app clipped-left>
      <v-app-bar-nav-icon
        v-if="authenticated"
        @click.stop="navigationDrawer = !navigationDrawer"
      />

      <v-toolbar-title class="pl-0">
        <router-link :to="{ name: 'home' }">
          <v-avatar size="40" tile>
            <v-img :src="appLogoUrl" :alt="appName" />
          </v-avatar>
        </router-link>
        <template v-if="!token && !authenticated">
          <span class="ml-1 d-sm-inline d-md-inline">
            {{ appName }}
          </span>
        </template>
        <template v-else-if="authenticated">
          <span class="ml-1 d-none d-md-inline">
            {{ appName }}
          </span>
        </template>
      </v-toolbar-title>

      <v-spacer />
      <template v-if="!token && !authenticated">
        <v-btn
          :to="{ name: 'login' }"
          light
          small
          class="secondary d-none d-md-flex"
          >{{ $t("Log in") }}</v-btn
        >
        <v-btn
          :to="{ name: 'register' }"
          v-if="!isPos"
          small
          class="primary ml-1 d-none d-md-flex"
          >{{ $t("register") }}</v-btn
        >
        <div class="d-flex align-center d-md-none">
          <v-btn
            :to="{ name: 'login' }"
            light
            x-small
            rounded
            class="primary mx-2"
            >{{ $t("Log in") }}</v-btn
          >
          <main-tabs-mobile />
        </div>
      </template>
      <template v-else-if="authenticated">

        <refresh-balance-btn/>
        <v-btn icon rounded class="px-2 width-auto">
          <v-icon>mdi-currency-usd-circle-outline</v-icon>
          <animated-number :number="accountBalance" />
        </v-btn>
        <v-menu v-model="userMenu" :close-on-content-click="false" offset-y>
          <template v-slot:activator="{ on }">
            <v-btn icon v-on="on">
              <v-avatar size="38">
                <img :src="user.avatar_url" :alt="user.name" />
              </v-avatar>
            </v-btn>
          </template>

          <v-card>
            <v-list class="pb-0">
              <v-list-item>
                <v-list-item-avatar>
                  <img :src="user.avatar_url" :alt="user.name" />
                </v-list-item-avatar>

                <v-list-item-content>
                  <v-list-item-title>{{ user.name }}</v-list-item-title>
                  <v-list-item-subtitle>{{ user.email }}</v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
              <v-list-item
                :to="{ name: 'users.show', params: { id: user.id } }"
                link
                exact
              >
                <v-list-item-icon>
                  <v-icon>mdi-account</v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title>{{ $t("Profile") }}</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
              <v-list-item :to="{ name: 'user.affiliate' }" link exact>
                <v-list-item-icon>
                  <v-icon>mdi-link</v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title>{{
                    $t("Affiliate program")
                  }}</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
              <v-list-item :to="{ name: 'user.account.transactions' }" link exact>
                <v-list-item-icon>
                  <v-icon>mdi-format-list-bulleted</v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title>{{ $t('Transactions') }}</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
              <v-list-item
                v-if="!isPos"
                :to="{ name: 'user.account.deposits.index' }"
                link
                exact
              >
                <v-list-item-icon>
                  <v-icon>mdi-cash-plus</v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title>{{ $t("Deposits") }}</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
              <v-list-item
                v-if="!isPos"
                :to="{ name: 'user.account.withdrawals.index' }"
                link
                exact
              >
                <v-list-item-icon>
                  <v-icon>mdi-cash-minus</v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title>{{ $t("Withdrawals") }}</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </v-list>

            <v-list class="pt-0">
              <v-subheader class="text-uppercase">
                {{ $t("Settings") }}
              </v-subheader>
              <v-list-item>
                <v-list-item-action>
                  <v-switch
                    class="spacing-v-switch"
                    :input-value="settings.sounds"
                    :value="settings.sounds"
                    :label="$t('Enable sounds')"
                    @change="saveSettings('sounds', $event)"
                  />
                </v-list-item-action>
              </v-list-item>
              <v-list-item>
                <v-list-item-action>
                  <v-switch
                    class="spacing-v-switch"
                    :input-value="$vuetify.theme.dark"
                    :value="$vuetify.theme.dark"
                    :label="$t('Dark Mode')"
                    @change="changeThemeMode"
                  />
                </v-list-item-action>
              </v-list-item>
            </v-list>

            <v-divider />

            <v-card-actions>
              <v-spacer />
              <v-btn text @click="logout">
                <v-icon>mdi-logout</v-icon>
                {{ $t("Log out") }}
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-menu>

        <v-btn v-if="chatEnabled" icon @click="onChatButtonClicked">
          <v-badge
            :content="unreadChatMessagesCount"
            :value="unreadChatMessagesCount"
            overlap
          >
            <v-icon>{{
              chatDrawer ? "mdi-message" : "mdi-message-outline"
            }}</v-icon>
          </v-badge>
        </v-btn>
      </template>

      <preloader :active="loading" />
    </v-app-bar>

    <!-- Main -->
    <v-main>
      <message />
      <router-view />
    </v-main>

    <!-- Footer -->
    <footer-menu />
  </v-app>
</template>

<script>
import { config } from "~/plugins/config";
import { mapState, mapGetters } from "vuex";
import Message from "~/components/Message";
import RefreshBalanceBtn from "~/components/RefreshBalanceBtn";
import Chat from "~/components/Chat";
import Preloader from "~/components/Preloader";
import FloatingButton from "~/components/FloatingButton";
import FooterMenu from "~/components/FooterMenu";
import AnimatedNumber from "~/components/AnimatedNumber";
import {
  navAdminItems,
  navUserItems,
  navAuthUserItems,
  externalGames
} from "./menu-items";
import MenuItem from "./menu-item";
import MainTabsMobile from "../components/MainMobile";
import Cookies from "js-cookie";
import { hasPermissionByRouteName } from "../router/permissions";

export default {
  name: "DefaultLayout",

  components: {
    FloatingButton,
    Message,
    Chat,
    Preloader,
    FooterMenu,
    AnimatedNumber,
    MenuItem,
    MainTabsMobile,
    RefreshBalanceBtn
  },

  data() {
    return {
      navigationDrawer: false,
      chatDrawer: false,
      accountMenu: false,
      settingsMenu: false,
      userMenu: false,
      unreadChatMessagesCount: 0,
      navAdminItems,
      navUserItems,
      navAuthUserItems,
      externalGames,
      showBanner: false,
      bannerMessage: "Nuevas actualizaciones Nuevas actualizaciones",
      chatClicked: false
    };
  },

  mounted() {
    this.getBanner();
  },

  computed: {
    ...mapState("auth", ["user", "account", "token"]),
    ...mapState("settings", ["settings"]),
    ...mapState("progress", ["loading"]),
    ...mapGetters("auth", [
      "is_user_admin",
      "is_user_seller",
      "is_user_supervisor",
      "is_user_manager",
      "is_user_customer_service",
      "is_user_financial_supervisor",
      "is_user_financial_assistant",
      "is_user_tracking"
    ]),
    ...mapGetters({
      authenticated: "auth/check",
      games: "package-manager/games"
    }),
    showInformationNav() {
      return hasPermissionByRouteName("changelog");
    },
    isPos() {
      return config("app.is_pos");
    },
    appName() {
      return config("app.name");
    },
    appLogoUrl() {
      return config("app.logo");
    },
    chatEnabled() {
      return config("settings.chat.enabled");
    },
    rafflePackageEnabled() {
      return this.$store.getters["package-manager/get"]("raffle");
    },
    paymentsPackageEnabled() {
      return this.$store.getters["package-manager/get"]("payments");
    },
    user_role_text() {
      if (this.is_user_admin) return this.$t("Admin");
      if (this.is_user_seller) return this.$t("Seller");
      if (this.is_user_supervisor) return this.$t("Supervisor");
      if (this.is_user_manager) return this.$t("Manager");
      if (this.is_user_customer_service) return this.$t("Customer Service");
      if (this.is_user_customer_service) return this.$t("Customer Service");
      if (this.is_user_financial_supervisor)
        return this.$t("Financial Supervisor");
      if (this.is_user_financial_assistant)
        return this.$t("Financial Assistant");
      if (this.is_user_tracking) return this.$t("User Tracking");
      else return "";
    },
    accountBalance() {
      if (this.account && this.account?.balance != null) {
        return this.account.balance;
      }

      return 0;
    },
    footerEnabled() {
      return config("settings.content.home.footer.enabled");
    }
  },

  methods: {
    config,
    setUnreadChatMessagesCount(count) {
      this.unreadChatMessagesCount = count;
    },
    onChatButtonClicked() {
      if (this.chatClicked) {
        this.chatDrawer = !this.chatDrawer;
      } else {
        this.chatClicked = true;
        setTimeout(() => {
          this.chatDrawer = !this.chatDrawer;
        }, 500);
      }
    },
    async logout() {
      // Log out
      await this.$store.dispatch("auth/logout");

      // Redirect to home page
      window.location.assign("/");
    },
    saveSettings(key, value) {
      // value is null when switch is turned off
      this.$store.dispatch("settings/set", { key, value: !!value });
    },
    changeThemeMode() {
      this.$vuetify.theme.dark = !this.$vuetify.theme.dark;
      Cookies.set("themeMode", this.$vuetify.theme.dark ? "dark" : "light", {
        expires: 365
      });
    },
    closeBanner() {
      this.showBanner = 0;
      Cookies.set("showBanner", 0, { expires: 7 });
    },
    getBanner() {
      if (!config("app.banner_message")) return;
      if (Cookies.get("bannerMessage") === config("app.banner_message")) {
        this.showBanner = Cookies.get("showBanner")
          ? Cookies.get("showBanner") === "true"
          : true;
      } else {
        this.showBanner = true;
        this.bannerMessage = config("app.banner_message");
        Cookies.set("showBanner", 1, { expires: 7 });
        Cookies.set("bannerMessage", config("app.banner_message"), {
          expires: 7
        });
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.theme--light.v-app-bar.v-toolbar.v-sheet {
  background-color: #fff;
}

.theme--dark .v-navigation-drawer {
  background: #274492 !important;
}

.theme--dark.v-app-bar.v-toolbar {
  background: #274492 !important;
}

.theme--dark.v-application {
  background: #274492 !important;
}
.spacing-v-switch::v-deep .v-label {
  margin-left: 1rem !important;
}
</style>
